import React from 'react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { Check, ChevronsUpDown } from 'lucide-react';

export default function AlertFilters({
  sortBy, setSortBy, priceFilter, setPriceFilter,
  sellerFilter, setSellerFilter, marketplaceFilter, setMarketplaceFilter,
  conditionFilter, setConditionFilter,
  sellerSearchOpen, setSellerSearchOpen, marketplaceSearchOpen, setMarketplaceSearchOpen,
  viewMode, setViewMode, uniqueSellers, uniqueMarketplaces, summary, scrapedListings, filteredListings
}) {
  return (
    <div className="bg-white rounded-xl shadow-sm p-4">
      <div className="flex flex-wrap gap-3">
        <div className="flex-1 min-w-[200px]">
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest First</SelectItem>
              <SelectItem value="price-low">💰 Best Deals (Lowest Price)</SelectItem>
              <SelectItem value="price-high">💎 Premium (Highest Price)</SelectItem>
              <SelectItem value="value">🎯 Best Value (Price/Storage)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex-1 min-w-[200px]">
          <Select value={priceFilter} onValueChange={setPriceFilter}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Prices</SelectItem>
              {summary.priceDistribution && summary.priceDistribution.map((range, idx) => range.count > 0 && (
                <SelectItem key={`range_${idx}`} value={`range_${idx}`}>{range.label} ({range.count})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex-1 min-w-[200px]">
          <Popover open={sellerSearchOpen} onOpenChange={setSellerSearchOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" role="combobox" className="w-full justify-between">
                {sellerFilter === 'all' ? 'All Sellers' : uniqueSellers.find(s => s.name === sellerFilter)?.name || 'Select seller...'}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[300px] p-0" align="start">
              <Command>
                <CommandInput placeholder="Search sellers..." />
                <CommandList>
                  <CommandEmpty>No seller found.</CommandEmpty>
                  <CommandGroup>
                    <CommandItem value="all" onSelect={() => { setSellerFilter('all'); setSellerSearchOpen(false); }}>
                      <Check className={`mr-2 h-4 w-4 ${sellerFilter === 'all' ? 'opacity-100' : 'opacity-0'}`} />
                      All Sellers ({scrapedListings.length})
                    </CommandItem>
                    {uniqueSellers.map(seller => (
                      <CommandItem key={seller.name} value={seller.name} onSelect={v => { setSellerFilter(v); setSellerSearchOpen(false); }}>
                        <Check className={`mr-2 h-4 w-4 ${sellerFilter === seller.name ? 'opacity-100' : 'opacity-0'}`} />
                        {seller.name} ({seller.count})
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        <div className="flex-1 min-w-[200px]">
          <Popover open={marketplaceSearchOpen} onOpenChange={setMarketplaceSearchOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" role="combobox" className="w-full justify-between">
                {marketplaceFilter === 'all' ? 'All Marketplaces' : uniqueMarketplaces.find(m => m.name === marketplaceFilter)?.name || 'Select marketplace...'}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[250px] p-0" align="start">
              <Command>
                <CommandInput placeholder="Search marketplaces..." />
                <CommandList>
                  <CommandEmpty>No marketplace found.</CommandEmpty>
                  <CommandGroup>
                    <CommandItem value="all" onSelect={() => { setMarketplaceFilter('all'); setMarketplaceSearchOpen(false); }}>
                      <Check className={`mr-2 h-4 w-4 ${marketplaceFilter === 'all' ? 'opacity-100' : 'opacity-0'}`} />
                      All Marketplaces ({scrapedListings.length})
                    </CommandItem>
                    {uniqueMarketplaces.map(marketplace => (
                      <CommandItem key={marketplace.name} value={marketplace.name} onSelect={v => { setMarketplaceFilter(v); setMarketplaceSearchOpen(false); }}>
                        <Check className={`mr-2 h-4 w-4 ${marketplaceFilter === marketplace.name ? 'opacity-100' : 'opacity-0'}`} />
                        {marketplace.name.charAt(0).toUpperCase() + marketplace.name.slice(1)} ({marketplace.count})
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        <div className="flex-1 min-w-[200px]">
          <Select value={conditionFilter} onValueChange={setConditionFilter}>
            <SelectTrigger><SelectValue placeholder="Item Condition" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="working">Working Only (Default)</SelectItem>
              <SelectItem value="all">All Conditions</SelectItem>
              <SelectItem value="damaged">AS-IS/Parts Only</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="text-sm text-gray-600 flex items-center px-3">
          Showing {filteredListings.length} of {scrapedListings.length}
        </div>
      </div>
    </div>
  );
}
